import React from 'react';
import FilterListRounded from '@mui/icons-material/FilterListRounded';
import type { FilterDef } from '../../../types/manualDashboard';
import {
  DateRangePicker,
  FilterBar,
  PageToolbar,
  SelectMenu,
  ToolbarButton,
  useCollapseOnScroll,
  useDebouncedCommit,
  type DateRange,
  type DateRangePreset,
} from '../../../ui-kit';

interface DashboardToolbarProps {
  dateRange: DateRange;
  onDateRangeChange: (next: DateRange) => void;
  datePresets: DateRangePreset[];
  /** Earliest and latest days the board can show. */
  minDate?: string;
  maxDate?: string;
  /** The day presets count back from, when it is not today — the newest day with complete data, say. */
  presetsEnd?: string;
  filters: FilterDef[];
  /** Applied to the board, i.e. what the numbers on screen reflect. */
  applied: Record<string, string[]>;
  onApply: (next: Record<string, string[]>) => void;
  loading: boolean;
  optionsLoading: boolean;
  /** Opening chat moves the scrollbar to the board column, so the fold resets on it. */
  chatOpen: boolean;
  /** How the date button reads: `month` for cohort boards, `day` where single days matter. */
  dateDisplay?: 'day' | 'month';
  /** Names the date control for screen readers. */
  dateLabel?: string;
  /**
   * A time zone menu, for boards whose times can be read in another zone. It
   * always sits right after the dates: which days, then which clock.
   */
  zone?: {
    label: string;
    value: string;
    options: { key: string; label: string }[];
    onChange: (key: string) => void;
  } | null;
  /** More controls on the top line after Filters — a planner's inputs, say. */
  extra?: React.ReactNode;
  /** The right end of the top line: a note on the dates used, say. */
  end?: React.ReactNode;
}

/**
 * The board's controls, assembled from the kit.
 *
 * Top layer: the date range, the time zone when the board has one, the
 * Filters toggle, then anything a board adds (`extra`). Lower layer: every filter. How current the data is and Chat sit in the page header above, as
 * on every dashboard; export lives on each table, since each exports itself.
 *
 * Filters apply as they are picked, after a short pause so ticking three
 * carriers sends one request rather than three.
 */
export default function DashboardToolbar({
  dateRange, onDateRangeChange, datePresets, minDate, maxDate, presetsEnd,
  filters, applied, onApply, loading, optionsLoading, chatOpen,
  dateDisplay = 'month', dateLabel = 'Cohort date range', zone, extra, end,
}: DashboardToolbarProps) {
  const [draft, setDraft] = useDebouncedCommit(applied, onApply, 300);

  // Filters fold away once the board is scrolled, so the sticky bar is one
  // slim line over the charts; the button shows them again, with a count of
  // what is applied so a folded bar never hides that the board is filtered.
  // Opening chat moves the scrollbar to the board column, hence the reset key.
  const toolbarRef = React.useRef<HTMLElement | null>(null);
  const filterPanel = useCollapseOnScroll(toolbarRef, { resetKey: chatOpen });
  const appliedCount = Object.values(applied).filter((v) => v.length > 0).length;
  const filtersId = React.useId();

  return (
    <PageToolbar
      ref={toolbarRef}
      ariaLabel="Board controls"
      loading={loading}
      secondaryOpen={filterPanel.shown}
      secondaryId={filtersId}
      start={
        <>
          <DateRangePicker
            value={dateRange}
            onChange={(next) => onDateRangeChange(next)}
            presets={datePresets}
            minDate={minDate}
            maxDate={maxDate}
            today={presetsEnd}
            // Weekly cohorts start on Monday, so the calendar does too.
            weekStartsOn={1}
            display={dateDisplay}
            // Every board names the shortcut it is on: "Last 90 days · …".
            showPresetLabel
            ariaLabel={dateLabel}
          />
          {/* Full names, since people know them as EST and CST as often as
              ET and CT; picked once and remembered, so a menu, not buttons. */}
          {zone && (
            <SelectMenu
              label={zone.label}
              value={zone.value}
              options={zone.options.map((o) => ({ value: o.key, label: `${o.label} (${o.key})` }))}
              onChange={zone.onChange}
              searchable={false}
            />
          )}
          <ToolbarButton
            icon={<FilterListRounded />}
            onClick={filterPanel.toggle}
            selected={filterPanel.shown}
            count={appliedCount}
            aria-expanded={filterPanel.shown}
            aria-controls={filtersId}
            tooltip={filterPanel.shown ? 'Hide filters' : 'Show filters'}
          >
            Filters
          </ToolbarButton>
          {extra}
        </>
      }
      end={end}
    >
      <FilterBar fields={filters} value={draft} onChange={setDraft} loading={optionsLoading} />
    </PageToolbar>
  );
}
